import { InternalRefetchQueriesInclude } from '@apollo/client'
import { Box, Button, Tag, Text } from '@factorialco/deprecated-design-system'
import { RichTextDisplay, RichTextEditor } from '@factorialco/f0-react/dist/experimental'
import { Form as FactorialForm } from 'factorial-form'
import {
  CreateFlexibleTimeRecordCommentDocument,
  CreateFlexibleTimeRecordCommentMutation,
  DeleteFlexibleTimeRecordCommentDocument,
  DeleteFlexibleTimeRecordCommentMutation,
  UpdateFlexibleTimeRecordCommentDocument,
  UpdateFlexibleTimeRecordCommentMutation,
} from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { formatProjectHours } from 'modules/project_management/lib/formatHours'
import { getProjectSubprojectName } from 'modules/project_management/lib/formatText'
import { CustomGlobalTimeRecord } from 'modules/project_management/lib/getCustomGlobalTimeRecordsForMyProjectsTable'

interface IAddCommentModalProps {
  customGlobalTimeRecord: CustomGlobalTimeRecord
  onClose: () => void
  currentDateProject:
    | {
        date: string
        imputedMinutes: number
        comment: string | null | undefined
        isFlexible?: boolean | undefined
        flexibleId?: string
        recordId?: string
      }
    | undefined
  refetchQueries: InternalRefetchQueriesInclude
}

const formSchema = { comment: 'string' }

const AddCommentModal = ({
  currentDateProject,
  customGlobalTimeRecord,
  refetchQueries,
  onClose,
}: IAddCommentModalProps) => {
  const form = useMemo(() => {
    const formAttributes = { comment: currentDateProject?.comment || '' }

    return new FactorialForm(formAttributes, formSchema)
  }, [currentDateProject?.comment])

  const contentHasBeenChanged = currentDateProject?.comment && form.get('comment').value !== null
  const contentHasBeenDeleted = currentDateProject?.comment && form.get('comment').value === null

  const { editorConfig } = useRichTextEditor()

  const [createFlexibleTimeRecordComment] = useMutation(CreateFlexibleTimeRecordCommentDocument, {
    extractorFn: (data: CreateFlexibleTimeRecordCommentMutation) =>
      data.projectManagementMutations.createProjectmanagementFlexibleTimeRecordComment,
    awaitRefetchQueries: true,
    refetchQueries: refetchQueries,
  })

  const [updateFlexibleTimeRecordComment] = useMutation(UpdateFlexibleTimeRecordCommentDocument, {
    extractorFn: (data: UpdateFlexibleTimeRecordCommentMutation) =>
      data.projectManagementMutations
        .updateByFlexibleTimeRecordProjectmanagementFlexibleTimeRecordComment,
    awaitRefetchQueries: true,
    refetchQueries: refetchQueries,
  })

  const [deleteFlexibleTimeRecordComment] = useMutation(DeleteFlexibleTimeRecordCommentDocument, {
    extractorFn: (data: DeleteFlexibleTimeRecordCommentMutation) =>
      data.projectManagementMutations
        .deleteByFlexibleTimeRecordProjectmanagementFlexibleTimeRecordComment,
    awaitRefetchQueries: true,
    refetchQueries: refetchQueries,
  })

  const flexibleId = currentDateProject?.flexibleId

  const changeFlexibleTimeRecordComment = async () => {
    if (contentHasBeenChanged && flexibleId) {
      await updateFlexibleTimeRecordComment({
        variables: {
          flexibleTimeRecordId: flexibleId,
          content: form.get('comment').value,
        },
      })
    } else if (contentHasBeenDeleted && flexibleId) {
      await deleteFlexibleTimeRecordComment({
        variables: {
          flexibleTimeRecordId: flexibleId,
        },
      })
    } else if (flexibleId) {
      await createFlexibleTimeRecordComment({
        variables: {
          flexibleTimeRecordId: flexibleId,
          content: form.get('comment').value,
        },
      })

      trackAmplitudeCreateCommentEvent()
    }
    onClose()
  }

  const trackAmplitudeCreateCommentEvent = () => {
    track(trackingEvents.projectManagement.flexibleTracking.createCommentInFlexibleTable, {
      projectId: customGlobalTimeRecord.projectId,
      subprojectId: customGlobalTimeRecord.subprojectId,
    })
  }

  return (
    <Modal
      illustration="document-edit"
      title={i18n.t('project_management.modals.comments.comment')}
      description={
        <Text size="300">{`${moment(currentDateProject?.date).format(
          'dddd, MMMM DD'
        )},${getProjectSubprojectName(
          `${customGlobalTimeRecord.projectName}`,
          customGlobalTimeRecord.subprojectName
        )}`}</Text>
      }
      onClose={onClose}
      onSubmit={() => changeFlexibleTimeRecordComment()}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          {currentDateProject?.isFlexible && <Button submit>{i18n.t('common.save')}</Button>}
        </>
      }
    >
      <Box gap="s16">
        <Box gap="s4">
          <Text size="200">{i18n.t('project_management.modals.comments.hours')}</Text>
          <Tag inline>{formatProjectHours(currentDateProject?.imputedMinutes)}</Tag>
        </Box>
        <Box gap="s4">
          <Text size="200">{i18n.t('project_management.modals.comments.comment')}</Text>
          {currentDateProject?.isFlexible ? (
            <Box data-testid="RichTextEditor">
              <RichTextEditor
                {...editorConfig}
                onChange={(data) => form.get('comment').set(data?.value)}
                placeholder={i18n.t('project_management.modals.comments.comment')}
                title={i18n.t('project_management.modals.comments.comment')}
                initialEditorState={{ content: form.get('comment').value || '' }}
              />
            </Box>
          ) : (
            <Box data-testid="RichTextDisplay">
              <RichTextDisplay content={currentDateProject?.comment || ''} />
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  )
}

export default observer(AddCommentModal)
